import { piiFormModel } from 'components/FormFields/PII';
import { currencyFormat } from 'utils';
import type { LooseObjectType } from 'utils/globalTypes';
import {
  ACCOUNT_EMAIL_ERROR_PROPERTIES,
  ACCOUNT_ERROR_MESSAGES,
  BILLING_TYPES,
  FIVE_STEP_AUTHORIZATIONS,
  FLEX_ALL_PLANS,
  FLEX_AUTHORIZATIONS,
  FLEX_BRAND_ERROR_MESSAGES,
  FLEX_ENROLLMENT_BRAND_PLANS,
  FLEX_DIT_VERIFY_DENY_CODE,
  FLEX_DIT_VERIFY_PROPERTY,
  AOL_AUTHORIZATIONS,
  ENROLLMENT_ERROR_PAGES,
  PARTICIPANT_TOKEN_ERROR_PAGES,
  PID_FAILURE_ERROR_CODES,
  PLAN_NAME_OVERRIDES,
} from './config';
import type {
  AolPayloadOptions,
  EnrollmentError,
  FlexPayloadOptions,
  IPersonalInfo,
  OfferSummaryInput,
} from './types';

const {
  phoneNumberModel: {
    formFields: { phoneNumber },
  },
  emailAddressModel: {
    formFields: { emailAddress },
  },
  birthDateModel: {
    formFields: { birthDate },
  },
  ssnModel: {
    formFields: { ssn },
  },
  nameModel: { groupId: nameGroupId },
  addressModel: { groupId: addressGroupId },
} = piiFormModel;

/* The Formik field every account-step email failure belongs against. */
const EMAIL_FIELD_NAME = emailAddress.name;

/**
 * Whether a redeemed offer enrols through the flex flow rather than the five-step flow, decided
 * entirely by `FLEX_ENROLLMENT_BRAND_PLANS` — a brand is either absent (five-step), listed
 * against `FLEX_ALL_PLANS` (every plan flex), or listed against the plan codes that are.
 */
export function isFlexEnrollmentOffer(brandCode: string, planCode: string) {
  const flexPlans = FLEX_ENROLLMENT_BRAND_PLANS[brandCode];

  if (!flexPlans) {
    return false;
  }

  return flexPlans === FLEX_ALL_PLANS || flexPlans.includes(planCode);
}

/**
 * `billingType` arrives with inconsistent casing across offers, and identityWeb compares it
 * case-sensitively in some places and lower-cased in others. Normalising once here means every
 * branch below matches the same way.
 */
function normaliseBillingType(billingType: string | null | undefined) {
  return billingType?.trim().toLowerCase() ?? '';
}

/**
 * Builds the `/enrollments/flex` request body from the account form's Formik values.
 *
 * Mirrors identityWeb's `FlexEnrollment` / `DMinorFlexEnrollment` models: Minor Defense
 * collects no date of birth, so `profile.birthDate` is omitted entirely rather than sent
 * empty. The DatePicker already stores `YYYY-MM-DD`, which is the server format.
 */
export function buildFlexEnrollmentPayload(
  formData: LooseObjectType,
  { offerCode, useCode, isCustomerBilled, isDminor }: FlexPayloadOptions,
) {
  const profile: LooseObjectType = {
    name: formData[nameGroupId],
    address: formData[addressGroupId],
    phoneNumber: formData[phoneNumber.name],
    authorizations: isCustomerBilled
      ? FLEX_AUTHORIZATIONS.billed
      : FLEX_AUTHORIZATIONS.complimentary,
  };

  if (!isDminor) {
    profile.birthDate = formData[birthDate.name];
  }

  return {
    offerCode,
    /* Complimentary enrollments are always redeemed from a code; direct-linked paid
     * offers may not carry one, so it is only sent when present. */
    ...(useCode ? { useCode } : {}),
    account: {
      userName: formData[emailAddress.name],
      password: formData.password,
    },
    profile,
  };
}

/**
 * Builds the `/enrollments/flex` request body for the AOL / partner single-page screen
 * (enrollment.service.ts:1107-1135).
 *
 * It posts to the same endpoint as a complimentary flex enrollment but carries the partner's
 * token as `yahooUserInfo`, and the consent set is fixed: the screen's two checkboxes gate the
 * submit without travelling in the body, exactly as flex's do.
 *
 * Only Financial Shield asks for an SSN — the field is absent from the Home Title Guardian
 * screen, so the key is omitted rather than sent empty. identityWeb marks `profile.ssn` required
 * for both brands while rendering it for one, which leaves its Home Title form permanently
 * invalid; gating the payload and the schema together is what makes that route usable.
 *
 * The confirm-SSN and confirm-password values never ship (identityWeb `@JsonIgnore`s them), and
 * there is no `country` — its `AolAddress` model has no such field.
 */
export function buildAolEnrollmentPayload(
  formData: LooseObjectType,
  { offerCode, token, sku, isFinancialShield }: AolPayloadOptions,
) {
  const profile: LooseObjectType = {
    name: formData[nameGroupId],
    address: formData[addressGroupId],
    phoneNumber: formData[phoneNumber.name],
    birthDate: formData[birthDate.name],
    authorizations: AOL_AUTHORIZATIONS,
  };

  if (isFinancialShield) {
    profile.ssn = formData[ssn.name];
  }

  return {
    offerCode,
    account: {
      userName: formData[emailAddress.name],
      password: formData.password,
    },
    profile,
    yahooUserInfo: { token, sku },
  };
}

/**
 * Which panel an unusable AOL token shows. identityWeb trusts `tokenStatus` off a 400 body
 * verbatim and treats every other failure — and a 400 without that field — as the generic one
 * (aol-single-page-enrollment.component.ts:92-98).
 */
export function resolveAolTokenState(error: unknown): 'used' | 'invalid' {
  let parsed: LooseObjectType | null = null;

  if (typeof error === 'string') {
    try {
      parsed = JSON.parse(error) as LooseObjectType;
    } catch {
      parsed = null;
    }
  } else if (error && typeof error === 'object') {
    parsed = error as LooseObjectType;
  }

  const status = Number(parsed?.status);
  const body = parsed?.data as LooseObjectType | undefined;

  return status === 400 && body?.tokenStatus === 'USED' ? 'used' : 'invalid';
}

/**
 * Whether a rejected AOL submit means the partner's token has already been redeemed.
 *
 * identityWeb reads `property` off the 400 body and swaps the whole form for the
 * already-active panel (aol-single-page-enrollment.component.ts:123-130); every other 400 stays
 * on the form or falls through to the generic panel.
 */
/**
 * Unpacks a rejection the interceptor exempted from string flattening.
 *
 * Those come through as `JSON.stringify({ status, data })` (services/api/apiAsync.ts), but a
 * rejection can also arrive as the object itself, and `data` is an array on the endpoints that
 * answer with a list of field errors. Returns `null` when the value is neither — a plain display
 * message, say.
 */
function parseExemptedRejection(error: unknown) {
  let parsed: LooseObjectType | null = null;

  if (typeof error === 'string') {
    try {
      parsed = JSON.parse(error) as LooseObjectType;
    } catch {
      return null;
    }
  } else if (error && typeof error === 'object') {
    parsed = error as LooseObjectType;
  }

  if (!parsed) {
    return null;
  }

  const { data } = parsed;
  return {
    status: parsed.status,
    body: (Array.isArray(data) ? data[0] : data) as LooseObjectType | undefined,
  };
}

export function isAolTokenUsedError(error: unknown) {
  const parsed = parseExemptedRejection(error);

  if (Number(parsed?.status) !== 400) {
    return false;
  }

  return parsed?.body?.property === 'token.used';
}

/**
 * Normalises a rejected enrollment request into a display message plus, where the failure is
 * terminal, the page identityWeb sends the member to.
 *
 * Both the flex and five-step endpoints are exempted from the interceptor's string flattening
 * (services/api/apiAsync.ts) so that `property` and `errorCode` survive — identityWeb routes on
 * both (flex-enrollment.component.ts:166-179, basic-info.component.ts:204-237,
 * enrollment-personal-details.component.ts:208-228).
 */
export function parseEnrollmentError(
  errorStr: unknown,
  fallbackMessage: string,
  options: { isFinancialShield?: boolean } = {},
): EnrollmentError {
  let error: LooseObjectType | null = null;

  if (typeof errorStr === 'string') {
    try {
      error = JSON.parse(errorStr);
    } catch {
      /* A plain string is already a display message. */
      return { message: errorStr || fallbackMessage };
    }
  } else if (errorStr && typeof errorStr === 'object') {
    error = errorStr as LooseObjectType;
  }

  if (!error) {
    return { message: fallbackMessage };
  }

  const { status } = error;
  const body = Array.isArray(error.data) ? error.data[0] : error.data;

  if (!body || typeof body !== 'object') {
    return { message: fallbackMessage };
  }

  const { message, property, errorCode } = body as LooseObjectType;

  if (
    typeof message === 'string' &&
    FLEX_BRAND_ERROR_MESSAGES.includes(message)
  ) {
    return {
      message: fallbackMessage,
      redirectTo: ENROLLMENT_ERROR_PAGES.brandNotValid,
    };
  }

  if (
    typeof property === 'string' &&
    property.includes(FLEX_DIT_VERIFY_PROPERTY) &&
    Number(errorCode) === FLEX_DIT_VERIFY_DENY_CODE
  ) {
    return {
      message: fallbackMessage,
      redirectTo: ENROLLMENT_ERROR_PAGES.idauthStatusDeny,
    };
  }

  /* Identity proofing rejected the member outright, or a credit freeze blocks the pull. */
  if (
    typeof errorCode === 'string' &&
    PID_FAILURE_ERROR_CODES.includes(errorCode)
  ) {
    return {
      message: fallbackMessage,
      redirectTo: ENROLLMENT_ERROR_PAGES.identityNotVerified,
    };
  }

  /**
   * The email field owns every duplicate/format failure the account step can produce.
   * identityWeb keys 400 off the offending `property` and gives Financial Shield its own
   * wording, then treats 406 and 409 as blanket email failures regardless of `property`.
   */
  const isEmailProperty =
    typeof property === 'string' &&
    (ACCOUNT_EMAIL_ERROR_PROPERTIES.includes(property) ||
      property.includes('username'));

  if (status === 400 && isEmailProperty) {
    return {
      message: options.isFinancialShield
        ? ACCOUNT_ERROR_MESSAGES.usernameExists
        : ACCOUNT_ERROR_MESSAGES.emailExists,
      fieldName: EMAIL_FIELD_NAME,
    };
  }

  if (status === 406) {
    return {
      message: ACCOUNT_ERROR_MESSAGES.invalidEmail,
      fieldName: EMAIL_FIELD_NAME,
    };
  }

  if (status === 409) {
    return {
      message: ACCOUNT_ERROR_MESSAGES.emailAlreadyTaken,
      fieldName: EMAIL_FIELD_NAME,
      /* identityWeb renders a "Want to sign in?" anchor inside this message; the page owns
         the link so the copy here stays a plain string. */
      showSignInLink: true,
    };
  }

  return {
    message: typeof message === 'string' && message ? message : fallbackMessage,
  };
}

/**
 * Where a rejected family-plan invitation lookup sends the member.
 *
 * The lookup is exempted from the interceptor's string flattening (services/api/apiAsync.ts)
 * precisely so this much survives: identityWeb shows a different page for each of
 * 400/406/410/416/500 (enrollment-main.component.ts:213-230), and anything else is a generic
 * failure.
 *
 * Those five are read off the response *body* rather than the HTTP status — identityWeb branches on
 * `err.error.errorCode` (enrollment.service.ts:588-592). The distinction matters: a spent
 * invitation answers `errorCode: 416` under an HTTP 400, so keying on the status alone would call
 * it an invalid token. The status is kept as a fallback for a failure that carries no code.
 */
export function resolveParticipantTokenErrorPage(error: unknown) {
  const parsed = parseExemptedRejection(error);
  const errorCode = parsed?.body?.errorCode;

  return (
    PARTICIPANT_TOKEN_ERROR_PAGES[Number(errorCode)] ??
    PARTICIPANT_TOKEN_ERROR_PAGES[Number(parsed?.status)] ??
    ENROLLMENT_ERROR_PAGES.somethingWentWrong
  );
}

/**
 * Some plan codes display a curated name rather than the offer's own
 * (utils/plan-name-mapping.ts).
 */
export function resolvePlanName(
  planCode: string | null | undefined,
  planName: string | null | undefined,
) {
  if (planCode && PLAN_NAME_OVERRIDES[planCode]) {
    return PLAN_NAME_OVERRIDES[planCode];
  }
  return planName ?? '';
}

/**
 * Builds the personal-info PATCH body.
 *
 * `confirmSsn` is collected and validated but deliberately absent: identityWeb marks it
 * `@JsonIgnore` (enrollment-personal-info.ts) and strips both SSN fields from its store on
 * success, so the number never persists anywhere on the client.
 */
export function buildPersonalInfoPayload(
  dateOfBirth: string,
  socialSecurityNumber: string,
): IPersonalInfo {
  return {
    birthDate: dateOfBirth,
    ssn: socialSecurityNumber,
    authorizations: FIVE_STEP_AUTHORIZATIONS.personalInfo,
  };
}

/**
 * True when the offer bills monthly. identityWeb accepts either the exact `MONTHLY` term name
 * or any term name merely containing "month" (enrollment-plan-summary.component.html:65).
 */
export function isMonthlyTerm(termName: string | null | undefined) {
  const normalised = termName?.trim().toLowerCase() ?? '';
  return normalised === 'monthly' || normalised.includes('month');
}

/**
 * The employee-benefit descriptions, one builder per billing type.
 *
 * Builders rather than a map of finished strings so only the branch that matches spends its
 * interpolation — the screen reads exactly one of these.
 */
const EMPLOYEE_BENEFIT_COPY_BUILDERS: Record<
  string,
  // eslint-disable-next-line no-unused-vars
  (planName: string, groupName: string) => string
> = {
  [BILLING_TYPES.ebCcBilled]: (planName, groupName) =>
    `${planName} is being offered to you as an employee benefit through ${groupName}. The monthly plan fee will be paid via your credit card. Your membership will remain active until you cancel your plan.`,
  [BILLING_TYPES.ebEmployerPaid]: (planName, groupName) =>
    `${planName} is being offered to you as an employee benefit through ${groupName}. Your monthly plan fee will be paid by ${groupName} until your plan is canceled through ${groupName}. In such a scenario, you will be provided the option to continue the service by paying via credit card.`,
  [BILLING_TYPES.ebPayrollDeduct]: (planName, groupName) =>
    `${planName} is being offered to you as an employee benefit through ${groupName}. The monthly plan fee will be converted to a payroll deduction amount based on ${groupName}'s pay cycle and deducted from each paycheck until your plan is canceled through ${groupName}. In such a scenario, you will be provided the option to continue the service by paying via credit card.`,
  [BILLING_TYPES.ebBuyUpCcBilled]: (planName, groupName) =>
    `${planName} is being offered to you as an employee benefit through ${groupName}. The plan you selected requires an additional payment. This portion will be paid via credit card and will remain active until you cancel, select another plan option or the plan is canceled through ${groupName}. If canceled, you will be provided the option to continue the service by paying via credit card.`,
};

/* identityWeb gives buy-up payroll deduct the same copy as plain payroll deduct. */
EMPLOYEE_BENEFIT_COPY_BUILDERS[BILLING_TYPES.ebBuyUpPayrollDeduct] =
  EMPLOYEE_BENEFIT_COPY_BUILDERS[BILLING_TYPES.ebPayrollDeduct];

/**
 * Resolves everything the plan-summary screen renders for a given offer.
 *
 * identityWeb spreads this across an `ngSwitch` over `billingType` plus a handful of nested
 * `ngIf`s (enrollment-plan-summary.component.html:11-103). Collapsing it into one pure resolver
 * keeps the screen declarative and makes every branch reachable from a unit test.
 *
 * The complimentary wording follows Figma (frames 14:17640 / 14:17664), which replaces
 * identityWeb's two-sentence version; every other branch has no Figma frame and so keeps the
 * Angular copy verbatim.
 */
export function resolveOfferSummaryCopy(offer: OfferSummaryInput) {
  const {
    planName,
    groupName,
    termDescription,
    termName,
    termEndDate,
    isFamilyPlan,
    primaryUserName,
    hasFreeTrial,
    hasPaidTrial,
    trialDuration,
    trialPriceCents,
    priceCents,
  } = offer;

  const billingType = normaliseBillingType(offer.billingType);
  const term =
    termDescription?.trim() || (isMonthlyTerm(termName) ? 'month' : 'year');
  const isComplimentary = billingType === BILLING_TYPES.complimentary;
  const primary = [primaryUserName?.first, primaryUserName?.last]
    .filter(Boolean)
    .join(' ');

  const base = {
    /* The grey plan card and the auto-renew consent are billed-only; a complimentary membership
       has nothing to charge for. The consent is deliberately *not* also gated on `isFamilyPlan` —
       identityWeb keeps it for a family invitee too and blocks the button until they accept
       (enrollment-plan-summary.component.html:86-108). */
    showOrderSummary: !isComplimentary,
    showSubscriptionConsent: !isComplimentary,
    /* A secondary enrollee is not the payer, so per-term pricing is meaningless to them. */
    showBillingTermRows: !isFamilyPlan,
    /* Nor is the charge itself theirs. Quoting a seven-day charge and an annual renewal directly
       under "your membership will be paid for by {primary}" contradicts it, so identityWeb drops
       this line for a family invitee as well as for a complimentary member
       (`*ngIf="!isFamilyPlan"`, enrollment-plan-summary.component.html:85). */
    disclaimer:
      isComplimentary || isFamilyPlan
        ? ''
        : `The initial charge may take up to seven days to process. We will not bill your account if we are unable to activate certain benefits. You will be billed the same amount each ${term} until your membership is canceled.`,
  };

  if (isComplimentary) {
    return {
      ...base,
      /* Plan name only, matching flex's create-account hero. Figma's `{brandName} {planName}`
         reads correctly for brands whose plan name omits the brand, but doubles it for those
         that embed it — "Identity Defense Identity Defense Complete". */
      description: `You are enrolling in ${planName}.`,
      complimentaryExpiry: `Your membership is complimentary and will expire on ${termEndDate}.`,
    };
  }

  /* identityWeb gates this paragraph on `isFamilyPlan` alone and simply interpolates an empty
     `{{primaryUserName}}` when the invitation carries no name
     (enrollment-billing-details.component.html:11-13). Falling through to the billing-type
     branches instead would promise an invitee a charge that is never made, so the branch keeps the
     sentence and drops the name out of it. */
  if (isFamilyPlan) {
    return {
      ...base,
      description: primary
        ? `Your membership will be paid for by ${primary}. Your membership will remain active until you cancel your plan or ${primary}'s account is canceled.`
        : 'Your membership will be paid for by the primary member on your plan. Your membership will remain active until you cancel your plan or their account is canceled.',
      complimentaryExpiry: '',
    };
  }

  const buildEmployeeBenefitCopy = EMPLOYEE_BENEFIT_COPY_BUILDERS[billingType];

  if (buildEmployeeBenefitCopy) {
    return {
      ...base,
      description: buildEmployeeBenefitCopy(planName, groupName),
      complimentaryExpiry: '',
    };
  }

  if (billingType === BILLING_TYPES.retailCcBilled && hasFreeTrial) {
    return {
      ...base,
      description: `You will receive ${planName} for ${trialDuration} days at no cost. When your free trial ends, you will be charged ${currencyFormat(priceCents)} (plus tax) each ${term} until your membership is canceled.`,
      complimentaryExpiry: '',
    };
  }

  if (billingType === BILLING_TYPES.retailCcBilled && hasPaidTrial) {
    return {
      ...base,
      description: `You will receive ${planName} for ${currencyFormat(trialPriceCents)} (plus tax) for the first ${trialDuration} days. When the trial period ends, you will be charged ${currencyFormat(priceCents)} (plus tax) each ${term} until your membership is canceled.`,
      complimentaryExpiry: '',
    };
  }

  /* A plain retail or company-billed offer shows the card alone — identityWeb's switch has no
     description branch for either (the commented-out `billingInfo.header` line). */
  return { ...base, description: '', complimentaryExpiry: '' };
}
